import styles from './Plans.module.scss';
import { faCircleNotch } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useStripe } from '@stripe/react-stripe-js';
import { useEffect, useRef, useState } from 'react';
import { Button, Card, Container } from 'react-bootstrap';
import { Redirect } from 'react-router-dom';
import { useFirebase } from '../../../contexts/FirebaseContext';
import { AppShell } from '../../components/AppShell/AppShell';
import { Footer } from '../../components/Footer/Footer';
import { FullscreenLoader } from '../../components/FullscreenLoader/FullscreenLoader';
import { HighlightedCard } from '../../components/HighlightedCard/HighlightedCard';
import { useCapacitor } from '../../../hooks/useCapacitor';
import { Browser } from '@capacitor/browser';
import { useTranslation } from 'react-i18next';
import { getPriceInterval } from '../../utils/utils';
import { Copyable } from '../../components/Copyable/Copyable';
import { Title } from '../../components/Title/Title';
import { useCurrentLanguage } from '../../../hooks/useCurrentLanguage';
import { If, Then } from 'react-if';
import { ConfirmationModal } from '../../components/ConfirmationModal/ConfirmationModal';
import { PageHeader } from '../../components/PageHeader/PageHeader';

const PlansComponent = () => {
  const { plans, subscription, addCheckoutSession, promoCodes, userDataFmt } = useFirebase();
  const { currentLanguage } = useCurrentLanguage();
  const stripe = useStripe();

  const { isNative } = useCapacitor();

  const [loading, setLoading] = useState(false);
  const [priceLoading, setPriceLoading] = useState<string>(null);
  const [visiblePromoCodes, setVisiblePromoCodes] = useState([]);
  const [visiblePlans, setVisiblePlans] = useState([]);

  const [checkoutSessionData, setCheckoutSessionData] = useState<{ sessionId: string; url: string }>(null);
  const [promoCodeReminderModal, setPromoCodeReminderModal] = useState(false);
  const skipFirstFalse = useRef(true);
  const { t } = useTranslation();

  useEffect(() => {
    if (!userDataFmt) return;
    if (!promoCodes?.length) return;
    const myCategory = userDataFmt.profile?.formData?.categoriaProfessionale;
    if (!['aso', 'odontoiatra'].includes(myCategory)) return;
    const _visiblePromoCodes = promoCodes.filter((promoCode) => promoCode.metadata?.CATEGORY == myCategory);
    setVisiblePromoCodes(_visiblePromoCodes);
  }, [promoCodes, userDataFmt]);

  useEffect(() => {
    if (!userDataFmt) return;
    if (!plans?.length) return;
    const myCategory = userDataFmt.profile?.formData?.categoriaProfessionale;
    if (!['aso', 'odontoiatra'].includes(myCategory)) return;
    const _visiblePlans = plans.filter((plan) => plan.metadata?.CATEGORY == myCategory);
    setVisiblePlans(_visiblePlans);
  }, [plans, userDataFmt]);

  useEffect(() => {
    if (promoCodeReminderModal) return;
    if (skipFirstFalse.current) {
      skipFirstFalse.current = false;
      return;
    }
    if (!checkoutSessionData) return;
    runPayment(checkoutSessionData).catch(console.error);
  }, [promoCodeReminderModal]);

  const buy = async (priceId: string, isEcm: boolean = false) => {
    setLoading(true);
    setPriceLoading(priceId);
    try {
      const { sessionId, url } = await addCheckoutSession(priceId, null, isEcm);
      if (!visiblePromoCodes.length && currentLanguage !== 'it') {
        await runPayment({ sessionId, url });
      } else {
        setCheckoutSessionData({ sessionId, url });
        setPromoCodeReminderModal(true);
      }
    } catch (e) {
      try {
        alert(JSON.stringify(e, null, 2));
      } catch {
        alert(e);
      }
    } finally {
      setLoading(false);
      setPriceLoading(null);
    }
  };

  const runPayment = async ({ sessionId, url }) => {
    if (isNative) {
      await Browser.open({ url });
    } else await stripe.redirectToCheckout({ sessionId });
  };

  const getActivePrice = (plan) => {
    const [activePrice] = plan.prices.filter((price) => price.active && !price.metadata?.UPGRADE);
    return activePrice;
  };

  const getLocalizedProperty = (item: any, prop: string) => {
    try {
      return item.metadata?.[`i18n.${prop}.${currentLanguage}`] || item[prop];
    } catch {
      return item[prop];
    }
  };

  if (subscription) {
    return <Redirect to={'/browse'} />;
  }

  return (
    <AppShell>
      <div className={styles.page}>
      <Title>{t('labels.piani')}</Title>

      <Container className={styles.content}>
        <PageHeader title={t('plans.labels.attivaAbbonamento')} />

        {!!visiblePromoCodes.length &&
          visiblePromoCodes.map((promoCode) => (
            <HighlightedCard key={promoCode.id} className="mt-2 mb-3 text-center">
              {t('plans.labels.utilizzaCodice')}{' '}
              <strong>
                <Copyable text={promoCode.code} />
              </strong>{' '}
              {t('plans.labels.utilizzaCodice2', {
                promoCode: (promoCode.coupon.amount_off / 100).toFixed(2),
                interval: getPriceInterval(promoCode.metadata?.interval, true) ? t(`labels.${getPriceInterval(promoCode.metadata?.interval, true)}`) : '',
              })}
            </HighlightedCard>
          ))}

        {!!visiblePlans.length &&
          visiblePlans.map((plan) => (
            <If condition={!!plan.stripe_metadata_ECM_INCLUDED && currentLanguage == 'it' || !plan.stripe_metadata_ECM_INCLUDED}>
              { /* I'll show the "Buy ECM option only if the language is italian and there is the metadata "ECM_INCLUDED" */}
              <Then>
                <Card className="surface-card mb-4" key={plan.id}>
                  <Card.Body>
                    <Card.Title className={`${styles.planTitle} text-center`}>{getLocalizedProperty(plan, 'name')}</Card.Title>
                    <div className={'text-center'}>{getLocalizedProperty(plan, 'description')}</div>

                    {/* <div className={'text-center mt-2'}>
                      {t('plans.labels.numeroMassimoDispositivi')} {getActivePrice(plan).metadata.DEVICES}
                    </div> */}

                    {Object.keys(plan.metadata)
                      .filter((key) => {
                        if (key.startsWith('i18n.description-')) return true;
                      })
                      .sort((a: any, b: any) => {
                        const positionA = parseInt(a.split('-')[1].split('.')[0], 10);
                        const positionB = parseInt(b.split('-')[1].split('.')[0], 10);
                        return positionA - positionB;
                      })
                      .map((descriptionLine, index) => {
                        const localizedProperty = getLocalizedProperty(plan, descriptionLine.split('.')[1]);
                        return (
                          <div className={'text-center'} key={index}>
                            {localizedProperty}
                          </div>
                        );
                      })}

                    <div className={`${styles.planPrice} text-center mt-3`}>
                      <strong>&euro; {plan.stripe_metadata_ECM_INCLUDED ? '365.00 +IVA' : (getActivePrice(plan).unit_amount / 100).toFixed(2)}</strong> /{' '}
                      {t(`labels.${getPriceInterval(getActivePrice(plan).interval) || 'emptyLabel'}`)}
                    </div>
                    <div className={'text-center mt-3'}>
                      <Button size={'lg'} className="tootor-button tootor-button--basic" disabled={loading} onClick={() => {
                        buy(getActivePrice(plan).id, !!plan.stripe_metadata_ECM_INCLUDED ? true : null);
                      }}>
                        {priceLoading == getActivePrice(plan).id ? (
                          <div>
                            <FontAwesomeIcon icon={faCircleNotch} spin className={'mr-2'} /> {t('labels.attendi')}&hellip;
                          </div>
                        ) : (
                          <div>{t('plans.labels.acquistaOra')}</div>
                        )}
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              </Then>
            </If>
          ))}

        {/* Card for shop redirect */}
        <If condition={currentLanguage == 'it' && userDataFmt.profile.formData.categoriaProfessionale !== 'aso'}>
          <Then>
            <Card className="surface-card mb-4" key={'ecm_shop_redirect'}>
              <Card.Body>
                <Card.Title className={`${styles.shopTitle} text-center mt-3`}>Visita lo shop ECM</Card.Title>
                 <div className={`text-center ${styles.plansEcmDescription}`}>
                  Visita il nostro shop per scoprire tutte le soluzioni orientate alla formazione ed i crediti ECM
                </div>
                <div className={'text-center mt-3'}>
                    <Button size={'lg'} className="tootor-button tootor-button--primary" disabled={loading} onClick={() => window.location.href = '/shop'}>
                    <b>{t('labels.scopriShop')}</b>
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Then>
        </If>

        <div className={'text-center text-muted mt-3'}>{t('plans.labels.potraiAnnullareAbbonamento')}</div>

        {!plans.length && <FullscreenLoader className={'mt-4'} fullscreen={false} />}
      </Container>

      <Footer />
      </div>

      <ConfirmationModal
        show={promoCodeReminderModal}
        onHide={() => {
          setLoading(false);
          setPriceLoading(null);
          setCheckoutSessionData(null);
          setPromoCodeReminderModal(false);
        }}
        title="Prima di procedere..."
        confirmLabel={t('labels.continua')}
        onConfirm={() => setPromoCodeReminderModal(false)}
      >
        {visiblePromoCodes.length ? (
            <>
              <div>
                <h2>{t('plans.labels.ricordaCodiceSconto')}</h2>
              </div>
              <div>{t('plans.labels.ricordaCodiceScontoDescrizione')}</div>
              {visiblePromoCodes.map((promoCode) => (
                <div key={promoCode.id}>
                  &bull;{' '}
                  <strong>
                    <Copyable text={promoCode.code} />
                  </strong>{' '}
                  {t('plans.labels.sulPiano', { interval: t(`labels.${getPriceInterval(promoCode.metadata?.interval, true) || 'emptyLabel'}`) })}
                </div>
              ))}
              <br />
            </>
          ) : null}
        {currentLanguage === 'it' ? (
            <div>
              <h2>{t('labels.confirmInfoModalSubtitle')}</h2>
              <div>
                {t('labels.confirmInfoModalDescription1')}<br />
                {t('labels.confirmInfoModalDescription2')}
              </div>
            </div>
          ) : null}
      </ConfirmationModal>
    </AppShell>
  );
};

export default PlansComponent;
